const ROOT_KEYS =
[
    "id",
    "oid",
    "email",
    "status",
    "fullName",
    "fullname",
    "initials",
    "firstName",
    "lastName",
    "secondLastName",
    "druId",
    "lastContractActive",
    "memberCode",
    "credentials",
    "mainUserIdentity",
    "savingsPlans",
    "user"
] as const;

const CREDENTIALS_KEYS = ["expires_at", "access_token"] as const;

const MAIN_USER_IDENTITY_KEYS = 
[
    "id",
    "firstName",
    "lastName",
    "secondLastName",
    "secondLastNam",
    "status",
    "countryPrefix",
    "phone",
    "birthday",
    "operationCountryPrefix",
    "operationPhone",
    "signCountryPrefix",
    "signPhone",
    "suitabilityTests"
] as const;

const SAVINGS_PLAN_ITEM_KEYS = ["id", "type", "status"] as const;

const USER_NESTED_KEYS = ["firstName", "lastName", "secondLastName"] as const;

function pick<T extends Record<string, unknown>>(obj: T | null | undefined, keys: readonly string[]): Partial<T>
{
    if(obj == null || typeof obj !== "object")
    {
        return {};
    }
    const result: Record<string, unknown> = {};
    for(const key of keys)
    {
        if(Object.prototype.hasOwnProperty.call(obj, key))
        {
            result[key] = (obj as Record<string, unknown>)[key];
        }
    }
    return result as Partial<T>;
}

function sanitizeCredentials(credentials: unknown): Record<string, unknown> | undefined
{
    if(credentials == null || typeof credentials !== "object")
    {
        return undefined;
    }
    return pick(credentials as Record<string, unknown>, CREDENTIALS_KEYS as unknown as string[]) as Record<string, unknown>;
}

function sanitizeMainUserIdentity(mainUserIdentity: unknown): Record<string, unknown> | undefined
{
    if(mainUserIdentity == null || typeof mainUserIdentity !== "object")
    {
        return undefined;
    }
    const obj = mainUserIdentity as Record<string, unknown>;
    const result = pick(obj, MAIN_USER_IDENTITY_KEYS as unknown as string[]) as Record<string, unknown>;
    if(Array.isArray(obj.suitabilityTests))
    {
        result.suitabilityTests = obj.suitabilityTests;
    }
    return Object.keys(result).length > 0 ? result : undefined;
}

function sanitizeSavingsPlanItem(item: unknown): Record<string, unknown> | undefined
{
    if(item == null || typeof item !== "object")
    {
        return undefined;
    }
    return pick(item as Record<string, unknown>, SAVINGS_PLAN_ITEM_KEYS as unknown as string[]) as Record<string, unknown>;
}

function sanitizeSavingsPlans(savingsPlans: unknown): Record<string, unknown>[] | undefined
{
    if(!Array.isArray(savingsPlans))
    {
        return undefined;
    }

    const arr = savingsPlans.map(sanitizeSavingsPlanItem)
        .filter((x): x is Record<string, unknown> => x != null && Object.keys(x).length > 0);
    return arr.length > 0 ? arr : undefined;
}

function sanitizeUserNested(user: unknown): Record<string, unknown> | undefined
{
    if(user == null || typeof user !== "object")
    {
        return undefined;
    }
    const result = pick(user as Record<string, unknown>, USER_NESTED_KEYS as unknown as string[]);
    return Object.keys(result).length > 0 ? (result as Record<string, unknown>) : undefined;
}

/**
 * Devuelve solo las propiedades permitidas del perfil para persistir en localStorage.
 * Cualquier otra propiedad del payload se ignora.
 */
export function sanitizeUserProfileForStorage(payload: unknown): Record<string, unknown>
{
    if(payload == null || typeof payload !== "object")
    {
        return {};
    }
    const raw = payload as Record<string, unknown>;
    const result: Record<string, unknown> = {};

    for(const key of ROOT_KEYS)
    {
        if(!Object.prototype.hasOwnProperty.call(raw, key))
        {
            continue;
        }
        const value = raw[key];
        if(value === undefined || value === null)
        {
            continue;
        }
        switch(key)
        {
            case "credentials":
                result.credentials = sanitizeCredentials(value);
                break;
            case "mainUserIdentity":
                result.mainUserIdentity = sanitizeMainUserIdentity(value);
                break;
            case "savingsPlans":
                result.savingsPlans = sanitizeSavingsPlans(value);
                break;
            case "user":
                result.user = sanitizeUserNested(value);
                break;
            default:
                result[key] = value;
                break;
        }
    }

    return result;
}

/**
 * Fusiona dos perfiles sanitizados: las propiedades del payload sobrescriben las del base
 * solo cuando el valor en payload no es undefined ni null.
 */
export function mergeSanitizedProfile(base: Record<string, unknown>, payload: Record<string, unknown>): Record<string, unknown>
{
    const result = { ...base };

    for(const key of Object.keys(payload))
    {
        const payloadValue = payload[key];
        if(payloadValue === undefined || payloadValue === null)
        {
            continue;
        }
        if(key === "credentials" && typeof payloadValue === "object" && payloadValue !== null)
        {
            const baseCreds = base.credentials as Record<string, unknown> | undefined;
            const mergedCreds = { ...(baseCreds || {}), ...(payloadValue as Record<string, unknown>)};
            result.credentials = mergedCreds;
            continue;
        }
        if(key === "mainUserIdentity" && typeof payloadValue === "object" && payloadValue !== null)
        {
            const baseMain = base.mainUserIdentity as Record<string, unknown> | undefined;
            const mergedMain = { ...(baseMain || {}), ...(payloadValue as Record<string, unknown>) };
            result.mainUserIdentity = mergedMain;
            continue;
        }
        if(key === "savingsPlans" && Array.isArray(payloadValue))
        {
            result.savingsPlans = payloadValue;
            continue;
        }
        if(key === "user" && typeof payloadValue === "object" && payloadValue !== null)
        {
            const baseUser = base.user as Record<string, unknown> | undefined;
            result.user = { ...(baseUser || {}), ...(payloadValue as Record<string, unknown>) };
            continue;
        }
        result[key] = payloadValue;
    }

    // No persistir usersSubscriptions: no se lee del perfil y se obtiene por API cuando hace falta.
    // Para revertir y volver a guardarlas: 
    // 1) Añadir "usersSubscriptions" a ROOT_KEYS; 
    // 2) En el switch de sanitizeUserProfileForStorage añadir case "usersSubscriptions": result.usersSubscriptions = sanitizeUsersSubscriptions(value); break; 
    // 3) Restaurar USER_SUBSCRIPTION_KEYS, SUBSCRIPTION_MIN_KEYS y las funciones sanitizeSubscriptionMin, sanitizeUsersSubscriptionItem y sanitizeUsersSubscriptions;
    // 4) En este merge, añadir el bloque: if(key === "usersSubscriptions" && Array.isArray(payloadValue)) { result.usersSubscriptions = payloadValue; continue; } 
    // 5) Eliminar la línea delete result.usersSubscriptions.
    delete result.usersSubscriptions;

    return result;
}